import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { segundosRestantes, useDesfazer } from '../../../app/desfazer';
import { BarraFixa, Icone } from '../../../ui';
import css from '../pedidos.module.css';

// A barra de baixo do balcão (decisões B3 e B4): "✓ Nº 1093, Edson Cardoso, saiu com Carlos." com o
// aviso ao cliente e o "Desfazer · 4 s". Montada uma vez no LayoutPainel; também mostra o erro do
// Desfazer e a recusa de uma saída ("O Rafael já saiu com este pedido pelo app.").

/** A recusa some sozinha depois disto, como o erro do Desfazer. */
const AVISO_MS = 10_000;

interface Aviso {
  id: number;
  texto: string;
}

// A recusa vem da lista ou da tela do pedido, e a barra mora no layout (de outra área): um aviso por vez,
// guardado aqui, evita um provedor novo só para isto.
let avisoAtual: Aviso | null = null;
let proximoAviso = 1;
const ouvintes = new Set<() => void>();

function avisarOuvintes() {
  for (const ouvir of ouvintes) ouvir();
}

/** Mostra um erro de gravação na barra (o Sai com recusado, o Pôr para sair que falhou). */
export function mostrarAvisoNaBarra(texto: string): void {
  avisoAtual = { id: proximoAviso++, texto };
  avisarOuvintes();
}

function limparAviso(id?: number) {
  if (!avisoAtual || (id !== undefined && avisoAtual.id !== id)) return;
  avisoAtual = null;
  avisarOuvintes();
}

function assinar(ouvir: () => void) {
  ouvintes.add(ouvir);
  return () => {
    ouvintes.delete(ouvir);
  };
}

function useAviso(): Aviso | null {
  return useSyncExternalStore(
    assinar,
    () => avisoAtual,
    () => null,
  );
}

/** "Desfazer · 4 s": conta enquanto a ação está na barra. */
function useSegundos(expiraEm: number | null): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (expiraEm === null) return;
    setAgora(Date.now());
    const id = setInterval(() => setAgora(Date.now()), 250);
    return () => clearInterval(id);
  }, [expiraEm]);
  return expiraEm === null ? 0 : segundosRestantes(expiraEm, agora);
}

export function BarraDeDesfazer() {
  const { atual, erro, desfazer, encerrar } = useDesfazer();
  const aviso = useAviso();
  const segundos = useSegundos(atual?.expiraEm ?? null);

  const botaoDesfazer = useRef<HTMLButtonElement>(null);

  // Ação nova manda: a recusa de antes já não vale. O botão que fez a ação some da linha ("Sai com" vira
  // "Na rua"): sem foco em lugar nenhum, ele vem para o Desfazer, que fica a um Enter (e não a 20 Tabs).
  const idDaAcao = atual?.id;
  useEffect(() => {
    if (idDaAcao === undefined) return;
    limparAviso();
    const focado = document.activeElement;
    if (!focado || focado === document.body || !focado.isConnected) botaoDesfazer.current?.focus();
  }, [idDaAcao]);

  const idDoAviso = aviso?.id;
  useEffect(() => {
    if (idDoAviso === undefined) return;
    const id = setTimeout(() => limparAviso(idDoAviso), AVISO_MS);
    return () => clearTimeout(id);
  }, [idDoAviso]);

  const problema = atual ? null : (erro ?? aviso?.texto ?? null);
  // O leitor de tela ouve o que aconteceu uma vez, não cada segundo da contagem.
  const anuncio = atual ? atual.texto : (problema ?? '');

  return (
    <>
      <span className="sr-only" aria-live="polite">
        {anuncio}
      </span>
      {atual && (
        <BarraFixa rotulo="Desfazer" tom="contraste" altura="aviso" vivo="off">
          <div className={css.conteudoDaBarra}>
            <span className={css.textoDaBarra}>
              <Icone nome="check" tamanho={18} />
              <b>{atual.texto}</b>
            </span>
            {atual.extra && (
              <a
                className={css.extraDaBarra}
                href={atual.extra.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  atual.extra?.aoUsar?.();
                  encerrar();
                }}
              >
                <Icone nome="conversa" tamanho={16} />
                {atual.extra.rotulo}
              </a>
            )}
            <button
              ref={botaoDesfazer}
              type="button"
              className={css.desfazerDaBarra}
              onClick={desfazer}
              disabled={atual.desfazendo}
              aria-label="Desfazer"
            >
              <Icone nome="desfazer" tamanho={16} />
              {atual.desfazendo ? 'Desfazendo…' : `Desfazer · ${segundos} s`}
            </button>
          </div>
        </BarraFixa>
      )}
      {problema && (
        <BarraFixa rotulo="Aviso" tom="erro" altura="aviso" vivo="off">
          <div className={css.conteudoDaBarra}>
            <span className={css.textoDaBarra}>
              <Icone nome="alerta" tamanho={18} />
              <b>{problema}</b>
            </span>
            <button
              type="button"
              className={css.fecharDaBarra}
              onClick={() => {
                encerrar();
                limparAviso();
              }}
            >
              Fechar
            </button>
          </div>
        </BarraFixa>
      )}
    </>
  );
}
